import picocolors from 'picocolors';

// Next.js / concurrently 等会 pipe stdout（isTTY=false），picocolors 默认会关色。
// 调试日志按人眼可读设计：未显式设置 NO_COLOR 时强制开色。
const pc = picocolors.createColors(!('NO_COLOR' in process.env));

type LogTag = string | undefined | null | false;

const TAG_COLORS = [pc.cyan, pc.yellow, pc.dim, pc.magenta] as const;

function formatPrefix(tags: LogTag[], error = false): string {
  return tags
    .filter((tag): tag is string => Boolean(tag))
    .map((tag, index) => {
      const color = error ? pc.red : TAG_COLORS[index % TAG_COLORS.length];
      return color(`[${tag}]`);
    })
    .join('');
}

function colorEvent(arg: unknown, error = false): unknown {
  if (typeof arg !== 'string' || !/^\[.+\]$/.test(arg)) return arg;
  if (error || /error|fail|refund/i.test(arg)) return pc.red(arg);
  if (/done|success|completed/i.test(arg)) return pc.green(arg);
  if (/start|begin|request|ensure|poll|wait/i.test(arg)) return pc.cyan(arg);
  return pc.magenta(arg);
}

function logWithPrefix(
  write: typeof console.log,
  prefix: string,
  args: any[],
  error = false,
) {
  if (args.length === 0) {
    write(prefix);
    return;
  }
  const [first, ...rest] = args;
  const event = colorEvent(first, error);
  // event tag（如 [start]）紧贴 prefix，避免 console.log 多参数插入空格
  if (typeof first === 'string' && /^\[.+\]$/.test(first)) {
    write(`${prefix}${event}`, ...rest);
    return;
  }
  write(prefix, event, ...rest);
}

/** 彩色 console.log，用法：createDlog('api.video.generate', modelId, rid) */
export function createDlog(...tags: LogTag[]) {
  const prefix = formatPrefix(tags);
  return (...args: any[]) => logWithPrefix(console.log, prefix, args);
}

/** 受开关控制的彩色 debug log */
export function createDebugDlog(enabled: boolean, ...tags: LogTag[]) {
  const log = createDlog(...tags);
  return (...args: any[]) => {
    if (enabled) log(...args);
  };
}

/** 彩色 console.error */
export function createElog(...tags: LogTag[]) {
  const prefix = formatPrefix(tags, true);
  return (...args: any[]) => logWithPrefix(console.error, prefix, args, true);
}

/** provider base 用的 debug/error logger */
export function createScopedLogger(options: {
  tags: LogTag[];
  debug?: boolean;
}) {
  const { tags, debug = true } = options;
  const dlog = createDebugDlog(debug, ...tags);
  const elog = createElog(...tags);
  return {
    debug: dlog,
    error: elog,
  };
}
